// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState, useEffect, useCallback } from 'react';
import { YearDropdown } from '../../components/ui';
import { Select } from '../../components/ui';
import ExpenseTrendChart from '../../components/ExpenseTrendChart';
import ProfileChart from '../../components/charts/ProfileChart';
import CurrentMonthChart from '../../components/charts/budget/CurrentMonthChart';
import { transactionAPI } from '../../api/budget';
import analyticsAPI from '../../api/analytics';
import { formatINR } from '../../utils/currency';
import {
  currentYear,
  currentMonth,
  MONTHS_IN_YEAR,
  getYearOrMonthRange,
} from '../../utils/dates';

const PERIODS = ['Monthly', 'Yearly'];

export default function Analysis() {
  const [period, setPeriod] = useState('Monthly');
  const [year, setYear] = useState(currentYear());
  const [month, setMonth] = useState(currentMonth());
  const [transactions, setTransactions] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getYearOrMonthRange(
        year,
        period === 'Monthly' ? month : null,
      );
      const params = { date_from: start, date_to: end };

      const [txRes, bdRes] = await Promise.all([
        transactionAPI.list(params),
        analyticsAPI.summary(params),
      ]);

      setTransactions(txRes.data?.results ?? txRes.data ?? []);
      setBreakdown(bdRes.data);
    } catch {
      // silent — charts render empty state
    } finally {
      setLoading(false);
    }
  }, [period, year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const monthOptions = MONTHS_IN_YEAR.reduce((acc, m, i) => {
    acc[i + 1] = m;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header + controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Visualise your spending patterns
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Period toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 transition-colors ${
                  period === p
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Year picker */}
          <YearDropdown selectedYear={year} onChange={setYear} />

          {/* Month picker — only in Monthly mode */}
          {period === 'Monthly' && (
            <Select
              value={month}
              onChange={setMonth}
              options={monthOptions}
              className="w-36"
            />
          )}
        </div>
      </div>

      {/* Summary strip */}
      {breakdown && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Expense', value: breakdown.total_expense },
            { label: 'Total Income', value: breakdown.total_income },
            { label: 'Total Savings', value: breakdown.total_savings },
            { label: 'Net Balance', value: breakdown.net_balance },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-white rounded-xl border border-gray-200 p-4"
            >
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-base font-semibold text-gray-900 mt-0.5">
                {formatINR(value ?? 0)}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Expense Trends
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 animate-pulse rounded-lg" />
          ) : (
            <ExpenseTrendChart
              period={period.toLowerCase()}
              year={year}
              month={month}
            />
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Category Breakdown
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 animate-pulse rounded-lg" />
          ) : (
            <ProfileChart data={transactions} />
          )}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Monthly Overview
          </h3>
          {loading ? (
            <div className="h-48 bg-gray-50 animate-pulse rounded-lg" />
          ) : (
            <CurrentMonthChart data={transactions} />
          )}
        </div>
      </div>
    </div>
  );
}
